import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert, Menu, MenuItem, ListItemIcon as MuiListItemIcon
} from '@mui/material';
import { Add, Edit, Delete, Star, MoreVert } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllFeatures, deleteFeature } from '../../services/Api';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllFeatures = () => {
    const navigate = useNavigate();
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedFeature, setSelectedFeature] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [featureToDelete, setFeatureToDelete] = useState(null);

    const fetchFeatures = async () => {
        setLoading(true);
        try {
            const response = await getAllFeatures();
            if (response.success) {
                setFeatures(response.data);
            } else {
                setError(response.message || 'Failed to fetch features');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching features');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeatures();
    }, []);

    const handleMenuOpen = (event, feature) => {
        setAnchorEl(event.currentTarget);
        setSelectedFeature(feature);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedFeature(null);
    };

    const handleDeleteClick = () => {
        setFeatureToDelete(selectedFeature);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        try {
            const response = await deleteFeature(featureToDelete.id);
            if (response.success) {
                setDeleteDialogOpen(false);
                fetchFeatures();
            } else {
                console.error(response.message || 'Failed to delete feature');
            }
        } catch (err) {
            console.error('An error occurred while deleting the feature:', err);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Star sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>Features</Typography>
                        <Typography variant="body2" color="text.secondary">Manage product meta features like 'Pure Veg', 'Contains Egg', etc.</Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/features/create')}
                    sx={{ borderRadius: '8px' }}
                >
                    Create Feature
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={40} />
                                    </TableCell>
                                </TableRow>
                            ) : features.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No features found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                features.map((feature, index) => (
                                    <TableRow key={feature.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            {feature.icon ? (
                                                <Box
                                                    component="img"
                                                    src={feature.icon}
                                                    alt={feature.label}
                                                    sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: '4px' }}
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{feature.key}</TableCell>
                                        <TableCell>{feature.label}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, feature)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: {
                        minWidth: 150,
                        border: '1px solid',
                        borderColor: 'text.border',
                        mt: 1,
                    }
                }}
            >
                <MenuItem onClick={() => { navigate(`/features/modify/${selectedFeature?.id}`, { state: { feature: selectedFeature } }); handleMenuClose(); }}>
                    <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>
                    Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
                    Delete
                </MenuItem>
            </Menu>

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Feature?"
                message={`Are you sure you want to delete the feature "${featureToDelete?.label}"?`}
            />
        </Box>
    );
};

export default GetAllFeatures;
